import React from "react";
import useReveal from "../hooks/useReveal";
import { MapPin, Phone, Clock, Mail } from "lucide-react";

const hours = [
  { d: "Montag", h: "08:00 – 18:00" },
  { d: "Dienstag", h: "08:00 – 18:00" },
  { d: "Mittwoch", h: "08:00 – 18:00" },
  { d: "Donnerstag", h: "08:00 – 18:00" },
  { d: "Freitag", h: "08:00 – 18:00" },
  { d: "Samstag", h: "Geschlossen" },
  { d: "Sonntag", h: "Geschlossen" },
];

export default function Contact() {
  const [ref, visible] = useReveal();
  return (
    <section
      id="kontakt"
      ref={ref}
      data-testid="contact-section"
      className={`relative bg-[#09090b] border-t border-zinc-900 transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-12 py-24 md:py-32">
        <div className="mb-14">
          <div className="text-[11px] tracking-[0.4em] uppercase gold-text mb-4">
            — Kontakt
          </div>
          <h2
            data-testid="contact-heading"
            className="font-display text-4xl md:text-6xl uppercase tracking-tight leading-[0.95]"
          >
            Besuchen Sie
            <br />
            <span className="gold-text">uns in Neuss.</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Map */}
          <div
            data-testid="contact-map"
            className="lg:col-span-7 relative border border-zinc-800 overflow-hidden min-h-[380px] md:min-h-[480px] bg-[#0f0f12]"
          >
            <iframe
              title="Autohaus Barwinski Karte"
              src="https://www.google.com/maps?q=Normannenstra%C3%9Fe+22%2C+41462+Neuss&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: 480, filter: "grayscale(0.7) contrast(1.1) invert(0.92) hue-rotate(180deg)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          {/* Details */}
          <div className="lg:col-span-5 grid gap-5">
            <InfoCard
              testid="contact-address"
              icon={MapPin}
              title="Adresse"
              lines={["Normannenstraße 22", "41462 Neuss"]}
              cta={{
                label: "Route planen",
                href: "https://www.google.com/maps/dir/?api=1&destination=Normannenstra%C3%9Fe+22%2C+41462+Neuss",
              }}
            />
            <InfoCard
              testid="contact-phone"
              icon={Phone}
              title="Telefon"
              lines={["02131 544154"]}
              cta={{ label: "Jetzt anrufen", href: "tel:02131544154" }}
            />
            <InfoCard
              testid="contact-email"
              icon={Mail}
              title="E-Mail"
              lines={["kfz-spitzer@gmx.de"]}
              cta={{ label: "E-Mail senden", href: "mailto:kfz-spitzer@gmx.de" }}
            />

            <div
              data-testid="contact-hours"
              className="border border-zinc-800 bg-[#0f0f12] p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="w-9 h-9 gold-bg grid place-items-center text-black">
                  <Clock size={16} />
                </span>
                <div className="font-display uppercase tracking-tight text-lg">
                  Öffnungszeiten
                </div>
              </div>
              <ul className="space-y-2">
                {hours.map((h) => (
                  <li
                    key={h.d}
                    className="flex items-center justify-between text-sm border-b border-zinc-800/60 pb-2 last:border-0 last:pb-0"
                  >
                    <span className="text-zinc-300">{h.d}</span>
                    <span className="text-zinc-400">{h.h}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function InfoCard({ icon: Icon, title, lines, cta, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-zinc-800 bg-[#0f0f12] p-6 hover:border-[#E0B33D]/60 transition-colors"
    >
      <div className="flex items-start gap-4">
        <span className="w-10 h-10 gold-bg grid place-items-center text-black flex-shrink-0">
          <Icon size={18} />
        </span>
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
            {title}
          </div>
          <div className="mt-1 text-white text-base font-medium">
            {lines.map((l) => (
              <div key={l}>{l}</div>
            ))}
          </div>
          {cta && (
            <a
              href={cta.href}
              target={cta.href.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer"
              className="mt-3 inline-block text-[11px] tracking-[0.3em] uppercase gold-text border-b border-[#E0B33D]/40 hover:border-[#E0B33D] pb-0.5"
            >
              {cta.label}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
